import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { reloadPage } from './reloadPage';

const RELOADED_FLAG_KEY = 'kirin:riskMapChunkReloaded';

/**
 * Vite throws this (not a typed error class) when the browser's cached
 * index.html still points at an asset hash a newer deploy has removed from
 * the CDN. Matches both the Chromium and Firefox wordings.
 */
function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return /dynamically imported module|importing a module script failed/i.test(
    error.message
  );
}

function hasAlreadyReloadedForStaleChunk(): boolean {
  try {
    return sessionStorage.getItem(RELOADED_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function markReloadedForStaleChunk(): boolean {
  try {
    sessionStorage.setItem(RELOADED_FLAG_KEY, '1');
    return true;
  } catch {
    // Quota, private mode, or disabled storage: the flag can't persist, so
    // reloading would loop forever instead of reaching the error boundary.
    return false;
  }
}

function clearReloadedForStaleChunk(): void {
  try {
    sessionStorage.removeItem(RELOADED_FLAG_KEY);
  } catch {
    // Quota, private mode, or disabled storage.
  }
}

/**
 * KIR: wraps a dynamic `import()` so a stale chunk left behind by a newer
 * deploy reloads the tab once to pick up the current asset manifest, instead
 * of surfacing the dashboard error boundary. Reloads at most once per
 * session so a genuinely broken chunk still falls through to the boundary
 * rather than reload-looping.
 */
export function lazyWithChunkReload<P>(
  factory: () => Promise<{ default: ComponentType<P> }>
): LazyExoticComponent<ComponentType<P>> {
  return lazy(async () => {
    try {
      const module = await factory();
      clearReloadedForStaleChunk();
      return module;
    } catch (error) {
      if (
        isStaleChunkError(error) &&
        !hasAlreadyReloadedForStaleChunk() &&
        markReloadedForStaleChunk()
      ) {
        reloadPage();
        // Suspense keeps showing its fallback until the reload replaces the page.
        return await new Promise<{ default: ComponentType<P> }>(() => {});
      }
      throw error;
    }
  });
}
